import { useCallback, useMemo, useRef } from "react";
import { TfiPlus } from "react-icons/tfi";
import { simpleAlert, simplePrompt } from "react-simple-dialogs";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
ModuleRegistry.registerModules([AllCommunityModule]);

import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useRecoilState, useSetRecoilState } from "recoil";

import type { Product } from "@pages/ProductCatalog/store";
import {
  blankNewProduct,
  NEW_PRODUCT_ID,
  newProductNameState,
  productSummaries,
  selectedProductState,
} from "@pages/ProductCatalog/store";

import { Button } from "./components/Button";
import { Toolbar } from "./components/Toolbar";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

export default function ProductsList() {
  const gridRef = useRef(null);
  const [products, setProducts] = useRecoilState(productSummaries);
  const [productId, setProductId] = useRecoilState(selectedProductState);
  const setNewProductName = useSetRecoilState(newProductNameState);

  const addNewProduct = useCallback(async () => {
    // Only one unsaved product at a time (it holds the sentinel id).
    if (products.find(({ id }) => id === NEW_PRODUCT_ID)) {
      await simpleAlert({
        message: "Finish saving the current new product first",
        closeLabel: "Close",
        title: "Error: Create new product",
      });
      return;
    }

    const name = await simplePrompt({
      title: "Create new product",
      message: "Enter a unique name (the checkout SKU) for the new product",
      confirmLabel: "Create",
      cancelLabel: "Cancel",
      inputLabel: "Name",
    });
    if (!name) {
      return;
    }

    if (products.find((product) => product.name === name)) {
      await simpleAlert({
        message: "Product name must be unique",
        closeLabel: "Close",
        title: "Error: Create new product",
      });
      return;
    }

    setNewProductName(name);
    setProducts((products) => [...products, { ...blankNewProduct(), name }]);
    setProductId(NEW_PRODUCT_ID);
  }, [products]);

  const columnDefs: ColDef<Product>[] = useMemo(
    () => [
      {
        headerName: "Product",
        field: "name",
      },
    ],
    [],
  );

  return (
    <div className="ag-theme-quartz w-full h-full">
      <Toolbar>
        <Button onClick={addNewProduct}>
          <TfiPlus />
        </Button>
      </Toolbar>
      <AgGridReact<Product>
        ref={gridRef}
        // @ts-ignore
        columnDefs={columnDefs}
        rowData={products}
        rowSelection="single"
        autoSizeStrategy={{ type: "fitCellContents" }}
        onSelectionChanged={({ api }) => {
          setProductId(api.getSelectedRows()[0]?.id);
        }}
        onRowDataUpdated={({ api }) =>
          api.forEachNode((node) =>
            node.setSelected(node.data?.id === productId),
          )
        }
      />
    </div>
  );
}
